import {
  DebtRepaymentPlan,
  DebtRepaymentPlanRequest,
  DebtRepaymentService,
  ReceivablesDebtRepayment,
} from '@vgrubs/components';
import { useState } from 'react';
import { planService } from '../helpers/repaymentPlanLabels';
import { RepaymentPlanCard } from './RepaymentPlanCard';
import { PastPlansModal } from './PastPlansModal';
import { RepaymentPlanModal } from './RepaymentPlanModal';
import { RepaymentsModal } from './RepaymentsModal';
import { StopRepaymentPlanModal } from './StopRepaymentPlanModal';

type Props = {
  locationId: number;
  /** The debt this section repays: the old vDrive debt or past SaaS fees. */
  service: DebtRepaymentService;
  debtRepayment: ReceivablesDebtRepayment;
  canEdit: boolean;
  busy: boolean;
  onSave: (
    plan: DebtRepaymentPlan | null,
    data: DebtRepaymentPlanRequest
  ) => Promise<boolean>;
  onStop: (plan: DebtRepaymentPlan, note: string | null) => Promise<boolean>;
};

/**
 * One service's way of paying old debt back by plan: the active plan (or the
 * way to start one), the plans before it, and every collection in a modal
 * behind the card's "Repayment history". Past plans only show once there are
 * some. The location detail carries both services in one `debtRepayment`;
 * this picks out its own.
 */
export const RepaymentPlanSection = ({
  locationId,
  service,
  debtRepayment,
  canEdit,
  busy,
  onSave,
  onStop,
}: Props) => {
  // `undefined` is shut; `null` is open for a new plan.
  const [editing, setEditing] = useState<DebtRepaymentPlan | null | undefined>(
    undefined
  );
  const [stopping, setStopping] = useState<DebtRepaymentPlan | null>(null);
  const [showAll, setShowAll] = useState(false);
  const [showPastPlans, setShowPastPlans] = useState(false);

  const isSaas = service === 'saas';
  const activePlan = isSaas
    ? debtRepayment.saasActivePlan ?? null
    : debtRepayment.activePlan;
  const repayableDebt = isSaas
    ? debtRepayment.saasRepayableDebt ?? 0
    : debtRepayment.repayableDebt;
  // Taking from the same money, the vDrive plan goes first.
  const runsAfterVDrive = isSaas && !!debtRepayment.activePlan;

  const plans = debtRepayment.plans.filter(
    (plan) => planService(plan) === service
  );
  const latest = debtRepayment.repayments.filter(
    (repayment) => planService(repayment) === service
  );
  // A backend from before Oct 22, 2026 has vDrive plans only, and its count
  // is theirs. From then on the count covers both services and there is no
  // count per service, so the link shows once a plan of this one has
  // collected anything.
  const bothServices = debtRepayment.saasRepayableDebt !== undefined;
  const hasRepayments = bothServices
    ? latest.length > 0 || plans.some((plan) => plan.repaidAmount > 0)
    : (debtRepayment.repaymentsTotal ?? debtRepayment.repayments.length) > 0;
  // The card already shows the active plan; this list is what came before.
  const pastPlans = plans.filter((plan) => plan.status !== 'active');

  return (
    <>
      <RepaymentPlanCard
        service={service}
        plan={activePlan}
        repayableDebt={repayableDebt}
        runsAfterVDrive={runsAfterVDrive}
        canEdit={canEdit}
        onCreate={() => setEditing(null)}
        onEdit={setEditing}
        onStop={setStopping}
        hasRepayments={hasRepayments}
        onShowRepayments={() => setShowAll(true)}
        pastPlansCount={pastPlans.length}
        onShowPastPlans={() => setShowPastPlans(true)}
      />

      <RepaymentPlanModal
        isOpen={editing !== undefined}
        locationId={locationId}
        service={service}
        plan={editing ?? null}
        repayableDebt={repayableDebt}
        runsAfterVDrive={runsAfterVDrive}
        busy={busy}
        onClose={() => setEditing(undefined)}
        onConfirm={(data) => onSave(editing ?? null, data)}
      />

      <RepaymentsModal
        isOpen={showAll}
        locationId={locationId}
        service={bothServices ? service : undefined}
        latest={bothServices ? latest : debtRepayment.repayments}
        total={debtRepayment.repaymentsTotal ?? debtRepayment.repayments.length}
        onClose={() => setShowAll(false)}
      />

      <PastPlansModal
        isOpen={showPastPlans}
        plans={pastPlans}
        onClose={() => setShowPastPlans(false)}
      />

      <StopRepaymentPlanModal
        service={service}
        plan={stopping}
        repayableDebt={repayableDebt}
        busy={busy}
        onClose={() => setStopping(null)}
        onConfirm={(note) =>
          stopping ? onStop(stopping, note) : Promise.resolve(false)
        }
      />
    </>
  );
};
